import React, { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Button from 'src/components/controls/forms/redesign/FormButton';
import SolarLease, { SolarLeaseHold } from 'src/models/contract_types/SolarLease';
import { useFetch } from 'use-http';
import { push, replace } from 'connected-react-router';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
// import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import User from 'src/models/User';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import ContractHoldDocs from '../LeasingComponents/redesign/ContractHoldDocs';
import actions from 'src/actions';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import DialogList from 'src/dialogs';
import AddWIOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddWIOwnership';
import ContractWorkingInterestList from '../LeasingComponents/redesign/ContractWorkingInterestList';
import PropertyUtil from 'src/util/PropertyUtil';
import Owner from 'src/models/tract_detail/Owner';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import Ownership from 'src/models/tract_detail/Ownership';
import WIContractOwnership from 'src/models/tract_detail/WIContractOwnership';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
// import MathUtil from 'src/util/MathUtil';
import AddOwnershipPages from './AddOwnershipPages';

const SolarLeaseInfo = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { register, handleSubmit, reset, errors, formState, getValues, setValue, watch } = useForm();
    const { get, put, post, response, del } = useFetch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const dispatch = useDispatch();

    // const maritalStatuses = [
    //     { value: 'a married man', label: 'a married man' },
    //     { value: 'a married woman', label: 'a married woman' },
    //     { value: 'a married person', label: 'a married person' },
    //     { value: 'a single man', label: 'a single man' },
    //     { value: 'a single woman', label: 'a single woman' },
    //     { value: 'a single person', label: 'a single person' },
    //     { value: 'a widower', label: 'a widower' },
    //     { value: 'a widow', label: 'a widow' },
    //     { value: 'husband and wife', label: 'husband and wife' },
    //     { value: 'wife and husband', label: 'wife and husband' },
    //     { value: 'husband and husband', label: 'husband and husband' },
    //     { value: 'wife and wife', label: 'wife and wife' },
    // ];

    const [solarLeaseData, setSolarLeaseData] = useState<SolarLease>(undefined);
    const [measurementTypes, setMeasurementTypes] = useState<Array<IndexReferenceType>>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [currentLeaseHold, setCurrentLeaseHold] = useState(0);
    const [selectedWIOwnershipId, setSelectedWIOwnershipId] = useState(0);
    const [royalty, setRoyalty] = useState(0);
    const [copyLeaseholdClicked, setCopyLeaseholdClicked] = useState<boolean>(false);
    const [sourceSelected, setSourceSelected] = useState<boolean>(false);

    const { isDirty } = formState;

    useEffect(() => {
        console.log('Solar Lease Page loaded');
        getMeasurementTypes();
        getUsers();
    }, []);

    useEffect(() => {
        if (solarLeaseData?.id > 0 && measurementTypes.length > 0) {
            console.log('Measurement types before reset', measurementTypes);
            reset(prepData(solarLeaseData, measurementTypes));
        }
    }, [solarLeaseData, currentLeaseHold, measurementTypes]);

    useEffect(() => {
        if (appContext.solarLeaseId > 0 && users.length > 0 && measurementTypes.length > 0) {
            getSolarLeaseData(appContext.solarLeaseId);
        }
    }, [users, measurementTypes]);

    useEffect(() => {
        if (solarLeaseData?.id > 0) {
            if (appContext.solarLeaseHoldId > 0 && solarLeaseData?.solarLeaseHolds?.findIndex((slh) => slh.id === appContext.solarLeaseHoldId) > -1) {
                const leaseHoldIndex = solarLeaseData?.solarLeaseHolds?.findIndex((slh) => slh.id === appContext.solarLeaseHoldId);
                setCurrentLeaseHold(leaseHoldIndex);
                setRoyalty(solarLeaseData?.solarLeaseHolds?.[leaseHoldIndex]?.decimalInterest);
            } else if (appContext.solarLeaseHoldId === 0) {
                const tempSolarLeaseData = solarLeaseData;
                tempSolarLeaseData.solarLeaseHolds.push(new SolarLeaseHold());
                setSolarLeaseData(tempSolarLeaseData);
                setCurrentLeaseHold(tempSolarLeaseData?.solarLeaseHolds?.length - 1);
                setRoyalty(0);
            }
        }
    }, [appContext.solarLeaseHoldId, solarLeaseData]);

    // Enable Copy Leasehold button when the selected Source changes from default
    const handleSourceSelected = (e: FormEvent<HTMLSelectElement>) => {
        const selectedItem = e.target as HTMLSelectElement;

        if (selectedItem.value !== '-- Select --') {
            setSourceSelected(true);
            setCopyLeaseholdClicked(true);
        }
    };        

    const prepData = useCallback(
        (roughLeaseData: SolarLease, measurementTypesList: Array<IndexReferenceType>) => {
            const formData: SolarLease = {
                ...roughLeaseData,
                createdDate: roughLeaseData.createdDate ? (moment(roughLeaseData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                optionDate: roughLeaseData.optionDate ? (moment(roughLeaseData.optionDate).format('YYYY-MM-DD') as unknown as Date) : null,
                effectiveDate: roughLeaseData.effectiveDate ? (moment(roughLeaseData.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null,
                rentDueDate: roughLeaseData.rentDueDate ? (moment(roughLeaseData.rentDueDate).format('YYYY-MM-DD') as unknown as Date) : null,
                reminderDate: roughLeaseData.reminderDate ? (moment(roughLeaseData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null,
                recordedDate: roughLeaseData.recordedDate ? (moment(roughLeaseData.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null,
                commitDate: roughLeaseData.commitDate ? (moment(roughLeaseData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null,
                inHandDate: roughLeaseData.inHandDate ? (moment(roughLeaseData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null,
                voidDate: roughLeaseData.voidDate ? (moment(roughLeaseData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null,

                currentHold: {
                    ...roughLeaseData.solarLeaseHolds?.[currentLeaseHold],
                    dueDiligenceEffectiveDate: roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.dueDiligenceEffectiveDate
                        ? (new Date(roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.dueDiligenceEffectiveDate).toISOString().split('T')[0] as unknown as Date)
                        : null,
                    optionEffectiveDate: roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.optionEffectiveDate
                        ? (new Date(roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.optionEffectiveDate)
                              .toISOString()
                              .split('T')[0] as unknown as Date)
                        : null,
                    constructionEffectiveDate: roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.constructionEffectiveDate
                        ? (new Date(roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.constructionEffectiveDate)
                              .toISOString()
                              .split('T')[0] as unknown as Date)
                        : null,
                    operationEffectiveDate: roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.operationEffectiveDate
                        ? (new Date(roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.operationEffectiveDate)
                              .toISOString()
                              .split('T')[0] as unknown as Date)
                        : null,
                    dueDiligenceEffectiveExpirationDate: roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.dueDiligenceEffectiveExpirationDate
                        ? (new Date(roughLeaseData.solarLeaseHolds?.[currentLeaseHold]?.dueDiligenceEffectiveExpirationDate)
                              .toISOString()
                              .split('T')[0] as unknown as Date)
                        : null,
                },
            };
            console.log('Measurement Types before calc:', measurementTypesList);
            if (formData.effectiveDate && formData.terms && formData.termsTypeId) {
                // tslint:disable-next-line:max-line-length
                formData.expirationDate = new Date(
                    calcExpirationDate(
                        formData.effectiveDate?.toString(),
                        formData.terms?.toString(),
                        formData.termsTypeId?.toString(),
                        measurementTypesList
                    )
                )
                    .toISOString()
                    .split('T')[0] as unknown as Date;
            }
            if (formData.currentHold.dueDiligenceEffectiveDate && formData.currentHold.dueDiligenceEffectiveTerm && formData.currentHold.dueDiligenceEffectiveTermTypeId) {
                // tslint:disable-next-line:max-line-length
                formData.currentHold.dueDiligenceEffectiveExpirationDate = new Date(
                    calcExpirationDate(
                        formData.currentHold?.dueDiligenceEffectiveDate?.toString(),
                        formData.currentHold?.dueDiligenceEffectiveTerm?.toString(),
                        formData.currentHold?.dueDiligenceEffectiveTermTypeId?.toString(),
                        measurementTypesList
                    )
                )
                    .toISOString()
                    .split('T')[0] as unknown as Date;
            }
            if (
                formData.currentHold.constructionEffectiveDate &&
                formData.currentHold.constructionTerm &&
                formData.currentHold.constructionTermTypeId
            ) {
                // tslint:disable-next-line:max-line-length
                formData.currentHold.constructionExpirationDate = new Date(
                    calcExpirationDate(
                        formData.currentHold?.constructionEffectiveDate?.toString(),
                        formData.currentHold?.constructionTerm?.toString(),
                        formData.currentHold?.constructionTermTypeId?.toString(),
                        measurementTypesList
                    )
                )
                    .toISOString()
                    .split('T')[0] as unknown as Date;
            }
            if (formData.currentHold.operationEffectiveDate && formData.currentHold.operationTerm && formData.currentHold.operationTermTypeId) {
                // tslint:disable-next-line:max-line-length
                formData.currentHold.operationExpirationDate = new Date(
                    calcExpirationDate(
                        formData.currentHold?.operationEffectiveDate?.toString(),
                        formData.currentHold?.operationTerm?.toString(),
                        formData.currentHold?.operationTermTypeId?.toString(),
                        measurementTypesList
                    )
                )
                    .toISOString()
                    .split('T')[0] as unknown as Date;
            }
            if (
                formData.currentHold.operationExtensionEffectiveDate &&
                formData.currentHold.operationExtensionTerm &&
                formData.currentHold.operationExtensionTermTypeId
            ) {
                // tslint:disable-next-line:max-line-length
                formData.currentHold.operationExtensionExpirationDate = new Date(
                    calcExpirationDate(
                        formData.currentHold?.operationExtensionEffectiveDate?.toString(),
                        formData.currentHold?.operationExtensionTerm?.toString(),
                        formData.currentHold?.operationExtensionTermTypeId?.toString(),
                        measurementTypesList
                    )
                )
                    .toISOString()
                    .split('T')[0] as unknown as Date;
            }
            if (formData.currentHold.optionEffectiveDate && formData.currentHold.optionTerm && formData.currentHold.optionTermTypeId) {
                // tslint:disable-next-line:max-line-length
                formData.currentHold.optionExpirationDate = new Date(
                    calcExpirationDate(
                        formData.currentHold?.optionEffectiveDate?.toString(),
                        formData.currentHold?.optionTerm?.toString(),
                        formData.currentHold?.optionTermTypeId?.toString(),
                        measurementTypesList
                    )
                )
                    .toISOString()
                    .split('T')[0] as unknown as Date;
            }

            // formData.initialBonus = calculateTotal(formData.bonusTypeId, formData.bonusAmount, formData.grossAcres, measurementTypesList);
            // formData.rentTotal = calculateTotal(formData.rentalAmount, formData.rentalTypeId, formData.grossAcres, measurementTypesList);
            formData.totalBonus = formData.initialBonus + formData.rentTotal + formData.addlBonusAmount + formData.flatBonus;

            // formData.currentHold.dueDiligenceFeeTotal = calculateTotal(
            //     formData.currentHold.dueDiligenceFeeTypeId,
            //     formData.currentHold.dueDiligenceFee,
            //     formData.grossAcres,
            //     measurementTypesList
            // );
            // formData.currentHold.constructionFeeTotal = calculateTotal(
            //     formData.currentHold.constructionFeeTypeId,
            //     formData.currentHold.constructionFee,
            //     formData.grossAcres,
            //     measurementTypesList
            // );
            // formData.currentHold.operatingIPFFeeTotal = calculateTotal(
            //     formData.currentHold.operatingIPFFeeTypeId,
            //     formData.currentHold.operatingIPFFee,
            //     formData.grossAcres,
            //     measurementTypesList
            // );
            // formData.currentHold.operatingOPFFeeTotal = calculateTotal(
            //     formData.currentHold.operatingOPFFeeTypeId,
            //     formData.currentHold.operatingOPFFee,
            //     formData.grossAcres,
            //     measurementTypesList
            // );
            // // tslint:disable-next-line:max-line-length
            // formData.currentHold.dueDiligenceExtensionFeeTotal = calculateTotal(
            //     formData.currentHold.dueDiligenceExtensionFeeTypeId,
            //     formData.currentHold.dueDiligenceExtensionFee,
            //     formData.grossAcres,
            //     measurementTypesList
            // );

            return formData;
        },
        [currentLeaseHold]
    );

    const getSolarLeaseData = async (solarLeaseId: number) => {
        const result: SolarLease = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/solarLease/${solarLeaseId}`
        );
        if (response.ok) {
            console.log('Solar Leasehold data, ', result);
            setSolarLeaseData(finalizeSolarLease(result));
        } else {
            window.alert('Get Solar Lease Data failed');
        }
    };

    const getMeasurementTypes = async () => {
        const result = await get(`/v1.0/referenceTypes`);

        if (response.ok) {
            setMeasurementTypes(result);
        }
    };

    const updateSolarLease = async (data: SolarLease) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/solarLease`,
            data
        );

        if (response.ok) {
            dispatch(
                push(window.location.pathname)
            );

            getSolarLeaseData(appContext.solarLeaseId);
        } else {
            window.alert('Update Solar Lease failed');
        }
    };

    const deleteSolarLeasehold = () => {
        const switchToLeaseholdId = solarLeaseData?.solarLeaseHolds.find((x) => x.id !== solarLeaseData?.solarLeaseHolds?.[currentLeaseHold].id).id;
        const deleteId = solarLeaseData?.solarLeaseHolds?.[currentLeaseHold].id;
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 1, 1);

        const confirmAction = async () => {
            // tslint:disable-next-line: max-line-length
            await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/solarLease/${deleteId}/false`);
            if (response.ok) {
                getSolarLeaseData(appContext.solarLeaseId);
                dispatch(
                    replace(
                        `${currentPath.join('/')}/${switchToLeaseholdId}`
                    )
                );
            } else {
                window.alert('delete leasehold failed');
            }
        };

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>Are you sure you want to delete this Leasehold?</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const deleteSolarLease = () => {
        const deleteId = solarLeaseData?.solarLeaseHolds?.[currentLeaseHold].id;
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 6, 6);

        const confirmAction = async () => {
            // tslint:disable-next-line: max-line-length
            await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/solarLease/${deleteId}/true`);
            if (response.ok) {
                dispatch(
                    replace(
                        `${currentPath.join('/')}`
                    )
                );
            } else {
                window.alert('delete leasehold failed');
            }
        };

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>This is the only leasehold. The lease will also be deleted. Continue?</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const createSolarLease = async (data: SolarLease) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/solarLease`,
            data
        );
        if (response.ok) {
            dispatch(
                replace(
                    `${currentPath.join('/')}/${result.contractId}/solar-lease-holds/${result.contractHoldId}`
                )
            );
            getSolarLeaseData(result.contractId);
        } else {
            window.alert('Create Solar Lease failed');
        }
    };

    const onCopyLeasehold = async () => {
        let copyLeaseholdId = getValues('sourceLeaseholdId');

        // tslint:disable-next-line: max-line-length
        const result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/solarLease/solar-lease-hold/${appContext.solarLeaseHoldId}/copy-leasehold/${copyLeaseholdId}`);

        if (response.ok) {
            let currentPath = window.location.pathname.split('/');
            currentPath.splice(currentPath.length - 1, 1);
    
            dispatch(
                push(`${currentPath.join('/')}/${result}`)
            );
            getSolarLeaseData(appContext.solarLeaseId);
        } else {
            window.alert('call failed');
        }
    };

    const leaseholdList = () => {
        if (solarLeaseData?.solarLeaseHolds?.length > 1) {
            let holds = [...solarLeaseData.solarLeaseHolds];
            holds.pop();
            let leaseholdIds = holds.map((lh: SolarLeaseHold) => {
                return { value: lh.id || 0, label: lh.number?.toString() || 'Error' };
            });

            return leaseholdIds;
        } else {
            return [];
        }
    };

    const onSubmit = (data: SolarLease) => {
        data.solarLeaseHolds = new Array<SolarLeaseHold>(data.currentHold);
        data = finalizeSolarLease(data);
        // data.id = leaseData.id;
        if (data.id > 0) {
            updateSolarLease(data);
        } else {
            data.isInHand = true;
            data.inHandDate = new Date();
            data.isCommitted = true;
            data.commitDate = new Date();
            createSolarLease(data);
        }
    };

    const finalizeSolarLease = (data: SolarLease) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.solarLeaseNumber = solarLeaseData.solarLeaseNumber ? solarLeaseData.solarLeaseNumber : 0;
        data.rteLegalDescription = solarLeaseData?.rteLegalDescription ? solarLeaseData.rteLegalDescription : '';

        data.createdByUserId = data.createdByUserId ? parseInt(data.createdByUserId + '', 10) : null;
        data.terms = data.terms ? parseInt(data.terms + '', 10) : null;
        data.termsTypeId = data.termsTypeId ? parseInt(data.termsTypeId + '', 10) : null;
        data.bonusTypeId = data.bonusTypeId ? parseInt(data.bonusTypeId + '', 10) : null;
        data.rentalTypeId = data.rentalTypeId ? parseInt(data.rentalTypeId + '', 10) : null;
        data.rentalFrequencyId = data.rentalFrequencyId ? parseInt(data.rentalFrequencyId + '', 10) : null;
        data.rentalTerms = data.rentalTerms ? parseInt(data.rentalTerms + '', 10) : null;
        data.rentalTermTypeId = data.rentalTermTypeId ? parseInt(data.rentalTermTypeId + '', 10) : null;
        
        data.grossAcres = data.grossAcres ? parseFloat(data.grossAcres + '') : 0;
        data.bonusAmount = data.bonusAmount ? parseFloat(data.bonusAmount + '') : 0;
        data.rentalAmount = data.rentalAmount ? parseFloat(data.rentalAmount + '') : 0;
        data.addlBonusAmount = data.addlBonusAmount ? parseFloat(data.addlBonusAmount + '') : 0;
        data.flatBonus = data.flatBonus ? parseFloat(data.flatBonus + '') : 0;
        
        data.isFiledByMemo = !!data.isFiledByMemo ? true : null;
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.fedStateLeaseNumber = data.fedStateLeaseNumber ? data.fedStateLeaseNumber : '';
        
        data.solarLeaseName = data.solarLeaseName ? data.solarLeaseName : '';
        data.lastName = data.lastName ? data.lastName : '';
        data.firstName = data.firstName ? data.firstName : '';
        data.companyName = data.companyName ? data.companyName : '';
        data.akaName = data.akaName ? data.akaName : '';
        data.spouseName = data.spouseName ? data.spouseName : '';
        data.spouseAkaName = data.spouseAkaName ? data.spouseAkaName : '';
        data.maritalStatus = data.maritalStatus ? data.maritalStatus : '';
        data.lessorOnDocument = data.lessorOnDocument ? data.lessorOnDocument : '';
        data.lessorSignatures = data.lessorSignatures ? data.lessorSignatures : '';
        data.leaseLessorNotes = data.leaseLessorNotes ? data.leaseLessorNotes : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.addlBonusDescription = data.addlBonusDescription ? data.addlBonusDescription : '';
        data.reminderNumber = data.reminderNumber ? data.reminderNumber : '';
        data.lesseeName = data.lesseeName ? data.lesseeName : '';
        
        data.createdDate = data.createdDate
        ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
        : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.optionDate = data.optionDate ? (moment(data.optionDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.effectiveDate = data.effectiveDate ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.rentDueDate = data.rentDueDate ? (moment(data.rentDueDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.recordedDate = data.recordedDate ? (moment(data.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null;

        if (!!solarLeaseData) {
            data.clientDocNumber = solarLeaseData?.clientDocNumber ? solarLeaseData.clientDocNumber : '';
            data.reminderNote = solarLeaseData?.reminderNote ? solarLeaseData.reminderNote : '';
            data.lesseeAddressLine1 = solarLeaseData?.lesseeAddressLine1 ? solarLeaseData.lesseeAddressLine1 : '';
            data.lesseeAddressLine2 = solarLeaseData?.lesseeAddressLine2 ? solarLeaseData.lesseeAddressLine2 : '';
            data.lesseeCity  = solarLeaseData?.lesseeCity ? solarLeaseData.lesseeCity : '';
            data.lesseeState = solarLeaseData?.lesseeState ? solarLeaseData.lesseeState : '';
            data.lesseeZipCode = solarLeaseData?.lesseeZipCode ? solarLeaseData.lesseeZipCode : '';
            data.ownersAddressOnDocument = solarLeaseData?.ownersAddressOnDocument ? solarLeaseData.ownersAddressOnDocument : '';
            data.ownersSSNTaxIdOnDocument = solarLeaseData?.ownersSSNTaxIdOnDocument ? solarLeaseData.ownersSSNTaxIdOnDocument : '';
            data.ownersOnDocument = solarLeaseData.ownersOnDocument ? solarLeaseData.ownersOnDocument : '';
    
            data.isNotOriginalLessee = solarLeaseData?.isNotOriginalLessee ? solarLeaseData.isNotOriginalLessee : null;
            data.isGeneratedLease = solarLeaseData?.isGeneratedLease ? solarLeaseData.isGeneratedLease : false;
            data.isVoid = solarLeaseData?.isVoid ? solarLeaseData.isVoid : false;
            data.isInHand = solarLeaseData?.isInHand ? solarLeaseData.isInHand : false;
            data.isCommitted = solarLeaseData?.isCommitted ? solarLeaseData.isCommitted : false;
            
            data.voidDate = solarLeaseData?.voidDate ? (moment(solarLeaseData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.commitDate = solarLeaseData?.commitDate ? (moment(solarLeaseData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.inHandDate = solarLeaseData?.inHandDate ? (moment(solarLeaseData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.reminderDate = solarLeaseData?.reminderDate ? (moment(solarLeaseData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }

        if (data.solarLeaseHolds?.length > 0) {
            let formatted = new Array<SolarLeaseHold>();
            data.solarLeaseHolds.forEach((solarLeaseHold: SolarLeaseHold) => {
                if (formatted.length === currentLeaseHold && !!data.currentHold) {
                    formatted.push(finalizeSolarLeaseHold(data.currentHold, data.grossAcres));
                } else {
                    formatted.push(finalizeSolarLeaseHold(solarLeaseHold, data.grossAcres));
                }
            });
            data.solarLeaseHolds = [...formatted];
        }

        return data;
    };

    const finalizeSolarLeaseHold = (data: SolarLeaseHold, grossAcres: number) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.solarLeaseId = solarLeaseData?.id ? solarLeaseData.id : 0;

        data.number = data.number ? parseInt(data.number + '', 10) : null;
        data.dueDiligenceEffectiveTerm = data.dueDiligenceEffectiveTerm ? parseInt(data.dueDiligenceEffectiveTerm + '', 10) : null;
        data.dueDiligenceEffectiveTermTypeId = data.dueDiligenceEffectiveTermTypeId ? parseInt(data.dueDiligenceEffectiveTermTypeId + '', 10) : null;

        data.optionTerm = data.optionTerm ? parseInt(data.optionTerm + '', 10) : null;
        data.optionTermTypeId = data.optionTermTypeId ? parseInt(data.optionTermTypeId + '', 10) : null;
        data.constructionTerm = data.constructionTerm ? parseInt(data.constructionTerm + '', 10) : null;
        data.constructionTermTypeId = data.constructionTermTypeId ? parseInt(data.constructionTermTypeId + '', 10) : null;
        data.operationTerm = data.operationTerm ? parseInt(data.operationTerm + '', 10) : null;
        data.operationTermTypeId = data.operationTermTypeId ? parseInt(data.operationTermTypeId + '', 10) : null;
        data.operationExtensionTerm = data.operationExtensionTerm ? parseInt(data.operationExtensionTerm + '', 10) : null;
        data.operationExtensionTermTypeId = data.operationExtensionTermTypeId ? parseInt(data.operationExtensionTermTypeId + '', 10) : null;
        data.dueDiligenceFeeTypeId = data.dueDiligenceFeeTypeId ? parseInt(data.dueDiligenceFeeTypeId + '', 10) : null;
        data.dueDiligenceExtensionFeeTypeId = data.dueDiligenceExtensionFeeTypeId ? parseInt(data.dueDiligenceExtensionFeeTypeId + '', 10) : null;
        data.constructionFeeTypeId = data.constructionFeeTypeId ? parseInt(data.constructionFeeTypeId + '', 10) : null;
        data.operatingIPFFeeTypeId = data.operatingIPFFeeTypeId ? parseInt(data.operatingIPFFeeTypeId + '', 10) : null;
        data.operatingOPFFeeTypeId = data.operatingOPFFeeTypeId ? parseInt(data.operatingOPFFeeTypeId + '', 10) : null;

        data.lessorAKA = data.lessorAKA ? data.lessorAKA : '';
        data.addlBonusDescription = data.addlBonusDescription ? data.addlBonusDescription : '';
        data.bonusAmount = data.bonusAmount ? parseInt(data.bonusAmount + '', 10) : null;
        data.bonusTypeId = data.bonusTypeId ? parseInt(data.bonusTypeId + '', 10) : null;
        data.rentalAmount = data.rentalAmount ? parseInt(data.rentalAmount + '', 10) : null;
        data.rentalTypeId = data.rentalTypeId ? parseInt(data.rentalTypeId + '', 10) : null;
        data.rentalFrequencyId = data.rentalFrequencyId ? parseInt(data.rentalFrequencyId + '', 10) : null;
        data.rentalTerms = data.rentalTerms ? parseInt(data.rentalTerms + '', 10) : null;
        data.rentalTermTypeId = data.rentalTermTypeId ? parseInt(data.rentalTermTypeId + '', 10) : null;
        data.addlBonusAmount = data.addlBonusAmount ? parseInt(data.addlBonusAmount + '', 10) : null;

        data.dueDiligenceFee = data.dueDiligenceFee ? parseFloat(data.dueDiligenceFee + '') : 0;
        data.dueDiligenceExtensionFee = data.dueDiligenceExtensionFee ? parseFloat(data.dueDiligenceExtensionFee + '') : 0;
        data.constructionFee = data.constructionFee ? parseFloat(data.constructionFee + '') : 0;
        data.operatingIPFFee = data.operatingIPFFee ? parseFloat(data.operatingIPFFee + '') : 0;
        data.operatingOPFFee = data.operatingOPFFee ? parseFloat(data.operatingOPFFee + '') : 0;
        data.escalatingPct = data.escalatingPct ? parseFloat(data.escalatingPct + '') : 0;

        data.isHistorical = data.isActive ? false : true;
        data.isActive = data.isActive ? data.isActive : false;

        data.dueDiligenceEffectiveDate = data.dueDiligenceEffectiveDate ? (moment(data.dueDiligenceEffectiveDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.optionEffectiveDate = data.optionEffectiveDate ? (moment(data.optionEffectiveDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.constructionEffectiveDate = data.constructionEffectiveDate
            ? (moment(data.constructionEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.operationEffectiveDate = data.operationEffectiveDate
            ? (moment(data.operationEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.operationExtensionEffectiveDate = data.operationExtensionEffectiveDate
            ? (moment(data.operationExtensionEffectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.rentDueDate = data.rentDueDate ? (moment(data.rentDueDate).format('YYYY-MM-DD') as unknown as Date) : null;

        if (data.contractOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.contractOwnerships.forEach((owner: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(owner));
            });
            data.contractOwnerships = [...formatted];
        }

        return data;
    };

    const finalizeWIOwnership = (data: WIContractOwnership) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = data.projectId ? parseInt(data.id + '', 10) : 0;
        data.ownerLookUpId = data.ownerLookUpId ? parseInt(data.ownerLookUpId + '', 10) : 0;
        data.parentOwnershipId = data.parentOwnershipId ? parseInt(data.parentOwnershipId + '', 10) : 0;
        data.ownershipId = data.ownershipId ? parseInt(data.ownershipId + '', 10) : 0;
        data.contractLookupId = data.contractLookupId ? parseInt(data.contractLookupId + '', 10) : 0;

        data.decimalInterest = data.decimalInterest ? parseFloat(data.decimalInterest + '') : 0;

        data.note = data.note ? data.note : '';

        if (data.orriOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.orriOwnerships.forEach((orri: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(orri));
            });
            data.orriOwnerships = [...formatted];
        }

        return data;
    };

    const getOptions = (type: string, item: string = '') => {
        if (measurementTypes?.length > 0) {
            return measurementTypes
                .filter((mt) => mt.description === type)
                .map((option) => {
                    if (type === 'Fee' && option.name === 'Per Item') {
                        return { value: option.id, label: `Per ${item}` };
                    }
                    return { value: option.id, label: option.name };
                });
        }
        return [];
    };

    const getUsers = async () => {
        const result = await get('v1.0/users/user-list');

        if (response.ok) {
            setUsers(result);
        }
    };

    const previousContractHold = () => {
        if (currentLeaseHold > 0) {
            const solarLeaseHoldId = solarLeaseData?.solarLeaseHolds?.[currentLeaseHold - 1]?.id;
            setCurrentLeaseHold(currentLeaseHold - 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/solar-leases/${appContext.solarLeaseId}/solar-lease-holds/${solarLeaseHoldId}`
                )
            );
        }
    };

    const nextContractHold = () => {
        if (currentLeaseHold + 1 < solarLeaseData?.solarLeaseHolds?.length) {
            const solarLeaseHoldId = solarLeaseData?.solarLeaseHolds?.[currentLeaseHold + 1]?.id;

            setCurrentLeaseHold(currentLeaseHold + 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/solar-leases/${appContext.solarLeaseId}/solar-lease-holds/${solarLeaseHoldId}`
                )
            );
        }
    };

    const handleExpirationChange = (type: string) => {
        let rep = getValues();

        switch (type) {
            case 'primary':
                setValue('expirationDate', calcExpirationDate(rep.effectiveDate, rep.terms, rep.termsTypeId, measurementTypes));
                break;
            case 'construction':
                // tslint:disable-next-line:max-line-length
                setValue(
                    'currentHold.constructionExpirationDate',
                    calcExpirationDate(
                        rep.currentHold.constructionEffectiveDate,
                        rep.currentHold.constructionTerm,
                        rep.currentHold.constructionTermTypeId,
                        measurementTypes
                    )
                );
                break;
            case 'effective':
                // tslint:disable-next-line:max-line-length
                setValue(
                    'currentHold.dueDiligenceEffectiveExpirationDate',
                    calcExpirationDate(
                        rep.currentHold.dueDiligenceEffectiveDate,
                        rep.currentHold.dueDiligenceEffectiveTerm,
                        rep.currentHold.dueDiligenceEffectiveTermTypeId,
                        measurementTypes
                    )
                );
                break;
            case 'operation':
                // tslint:disable-next-line:max-line-length
                setValue(
                    'currentHold.operationExpirationDate',
                    calcExpirationDate(
                        rep.currentHold.operationEffectiveDate,
                        rep.currentHold.operationTerm,
                        rep.currentHold.operationTermTypeId,
                        measurementTypes
                    )
                );
                break;
            case 'operationExtension':
                // tslint:disable-next-line:max-line-length
                setValue(
                    'currentHold.operationExtensionExpirationDate',
                    calcExpirationDate(
                        rep.currentHold.operationExtensionEffectiveDate,
                        rep.currentHold.operationExtensionTerm,
                        rep.currentHold.operationExtensionTermTypeId,
                        measurementTypes
                    )
                );
                break;
            case 'option':
                // tslint:disable-next-line:max-line-length
                setValue(
                    'currentHold.optionExpirationDate',
                    calcExpirationDate(
                        rep.currentHold.optionEffectiveDate,
                        rep.currentHold.optionTerm,
                        rep.currentHold.optionTermTypeId,
                        measurementTypes
                    )
                );
                break;
            default:
                break;
        }
    };

    // const handleTotals = () => {
    //     let rep = getValues();
    //     let totalBonus = 0;
    //     let grossAcres = !isNaN(parseFloat(rep.grossAcres)) ? parseFloat(rep.grossAcres) : 0;

    //     let bonusTypeId = parseInt(rep.bonusTypeId, 10);
    //     let bonusAmount = parseFloat(rep.bonusAmount);
    //     if (!isNaN(bonusTypeId) && !isNaN(bonusAmount)) {
    //         let initialBonus = calculateTotal(bonusTypeId, bonusAmount, grossAcres, measurementTypes);
    //         setValue('initialBonus', initialBonus);
    //         totalBonus += initialBonus;
    //     }

    //     let rentalTypeId = parseInt(rep.rentalTypeId, 10);
    //     let rentAmount = parseFloat(rep.rentalAmount);
    //     if (!isNaN(rentalTypeId) && !isNaN(rentAmount)) {
    //         let totalRent = calculateTotal(rentalTypeId, rentAmount, grossAcres, measurementTypes);
    //         setValue('totalRent', totalRent);
    //         totalBonus += totalRent;
    //     }

    //     let flatBonus = !isNaN(parseFloat(rep.flatBonus)) ? parseFloat(rep.flatBonus) : 0;
    //     let additionalBonus = !isNaN(parseFloat(rep.addlBonusAmount)) ? parseFloat(rep.addlBonusAmount) : 0;

    //     totalBonus += flatBonus + additionalBonus;
    //     setValue('totalBonus', totalBonus);

    //     let dueDiligenceType = parseInt(rep.currentHold.dueDiligenceFeeTypeId, 10);
    //     let dueDiligence = parseFloat(rep.currentHold.dueDiligenceFee);
    //     if (!isNaN(dueDiligence) && !isNaN(dueDiligenceType)) {
    //         setValue('currentHold.dueDiligenceFeeTotal', calculateTotal(dueDiligenceType, dueDiligence, grossAcres, measurementTypes));
    //     }

    //     let dueDiligenceExtensionFeeType = parseInt(rep.currentHold.dueDiligenceExtensionFeeTypeId, 10);
    //     let dueDiligenceExtensionFee = parseFloat(rep.currentHold.dueDiligenceExtensionFee);
    //     if (!isNaN(dueDiligenceExtensionFee) && !isNaN(dueDiligenceExtensionFeeType)) {
    //         setValue(
    //             'currentHold.dueDiligenceExtensionFeeTotal',
    //             calculateTotal(dueDiligenceExtensionFeeType, dueDiligenceExtensionFee, grossAcres, measurementTypes)
    //         );
    //     }

    //     let constructionFeeType = parseInt(rep.currentHold.constructionFeeTypeId, 10);
    //     let constructionFee = parseFloat(rep.currentHold.constructionFee);
    //     if (!isNaN(constructionFee) && !isNaN(constructionFeeType)) {
    //         setValue('currentHold.constructionFeeTotal', calculateTotal(constructionFeeType, constructionFee, grossAcres, measurementTypes));
    //     }

    //     let operatingIPFFee = parseInt(rep.currentHold.operatingIPFFee, 10);
    //     let operatingIPFFeeType = parseFloat(rep.currentHold.operatingIPFFeeTypeId);
    //     if (!isNaN(operatingIPFFee) && !isNaN(operatingIPFFeeType)) {
    //         setValue('currentHold.operatingIPFFeeTotal', calculateTotal(operatingIPFFeeType, operatingIPFFee, grossAcres, measurementTypes));
    //     }

    //     let operatingOPFFee = parseInt(rep.currentHold.operatingOPFFee, 10);
    //     let operatingOPFFeeType = parseFloat(rep.currentHold.operatingOPFFeeTypeId);
    //     if (!isNaN(operatingOPFFee) && !isNaN(operatingOPFFeeType)) {
    //         setValue('currentHold.operatingOPFFeeTotal', calculateTotal(operatingOPFFeeType, operatingOPFFee, grossAcres, measurementTypes));
    //     }
    // };

    // const calculateTotal = (
    //     measurementTypeId: number,
    //     paymentAmount: number,
    //     grossAcres: number,
    //     measurementTypesList: Array<IndexReferenceType>
    // ) => {
    //     let measurementType = measurementTypesList.find((mt) => mt.id === measurementTypeId)?.name;

    //     return measurementType !== undefined ? Number(MathUtil.cleanDollarAmount(measurementType === 'Per Item' ? paymentAmount * grossAcres : paymentAmount)) : 0;
    // };

    const calcExpirationDate = (
        effectiveDateField: string,
        termField: string,
        termTypeField: string,
        measurementTypesList: Array<IndexReferenceType>
    ) => {
        const term = !isNaN(parseInt(termField, 10)) ? parseInt(termField, 10) : undefined;
        const termUnit = termTypeField ? termTypeField : undefined;
        const effectiveDate = effectiveDateField ? new Date(effectiveDateField) : undefined;

        if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate) {
            console.log('Measurement Types during calc:', measurementTypesList);
            let termString = measurementTypesList.find((mt) => mt.id === parseInt(termUnit, 10))?.name;
            switch (termString) {
                case 'Years':
                    return new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0];
                case 'Months':
                    return new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0];
                case 'Days':
                    return new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0];
                default:
                    return '';
            }
        } else {
            return '';
        }
    };

    const onSaveAddOwnership = async (values: AddWIOwnershipData) => {
        values.contractOwnership.contractLookupId = solarLeaseData.solarLeaseHolds[currentLeaseHold].contractLookupId;

        await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/solarLease/contract-ownership`,
            values
        );

        if (response.ok) {
            // dispatch(
            //     push(
            //         `/clients/${appContext.clientNumber}/projects/${appCont
            // ext.projectNumber}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/ow
            // nership/${appContext.ownershipId}/solar-leases/${appContext.solarLeaseId}/solar-lease-holds/${appContext.solarLeaseHoldId}`
            //     )
            // );
            getSolarLeaseData(appContext.solarLeaseId);
        } else {
            window.alert('Update Contract Ownership failed');
        }
    };

    const onTryAddWI = () => {
        if (!isNaN(solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.id)) {
            dialogContext.contract.addContractOwnership(
                DialogList.ADD_WI_OWNERSHIP,
                'Add Working Interest Ownership',
                undefined,
                undefined,
                appContext.clientNumber,
                appContext.projectNumber,
                AddOwnershipPages.AddOwnershipLandingPage,
                (path: string) => dispatch(push(path)),
                null,
                onSaveAddOwnership
            );
        } else {
            dispatch(actions.layout.showDialog(DialogList.LEASEHOLD_SAVE_BEFORE_ADDING_WI));
        }
    };

    const onAddORRI = (newParentOwnershipId: number) => {
        dialogContext.contract.addContractOwnership(
            DialogList.ADD_ORRI_OWNERSHIP,
            'Add Overriding Royalty Interest Ownership',
            undefined,
            newParentOwnershipId,
            appContext.clientNumber,
            appContext.projectNumber,
            AddOwnershipPages.AddOwnershipLandingPage,
            (path: string) => dispatch(push(path)),
            null,
            onSaveAddOwnership
        );
    };

    const onCopyORRI = (ownershipId: number) => {
        dialogContext.leasehold.cloneORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), () => location.reload());
    };

    const ownerNameForWorkingInterest = (workingInterestId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.contractOwnerships);
        const ownership = wiOwnerships?.filter((wio: Ownership) => wio.id === workingInterestId)[0];
        const owner = wiOwners.filter((o: Owner) => o.ownerLookUpId === ownership.ownerLookUpId)[0];

        return owner?.name || '[Error]';
    };

    const onDeleteORRI = (ownershipId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.contractOwnerships);
        const ownership: WIOwnership = wiOwnerships?.filter((wio: WIOwnership) => wio.id === ownershipId)[0];

        dialogContext.leasehold.deleteORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), ownership.orriOwnerships, wiOwners, () =>
            location.reload()
        );
    };

    const onDeleteContractOwnership = async (contractOwnershipId: number) => {
        const result: SolarLease = await del(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/solarLease/contract-ownership/${contractOwnershipId}`
        );
        if (response.ok) {
            console.log('Successfully Delete Contract Ownership ', result);
            dispatch(actions.layout.hideDialog());
            getSolarLeaseData(appContext?.solarLeaseId);
        } else {
            window.alert('Get Surface Waiver Data failed');
        }
    };

    const onUpdateWINote = async (ownership: Ownership) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/surfaceTracts/notes`,
            ownership
        );

        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getSolarLeaseData(appContext.solarLeaseId);
        } else {
            window.alert(`update wi note failed. note: ${ownership.note}`);
        }
    };

    const findOwnership = (ownershipId: number = 0) => {
        let refId = ownershipId > 0 ? ownershipId : selectedWIOwnershipId;
        let ownership = solarLeaseData.solarLeaseHolds[currentLeaseHold].contractOwnerships?.find(
            (lo: WIContractOwnership) => lo.id === refId
        );

        if (!ownership) {
            solarLeaseData.solarLeaseHolds[currentLeaseHold].contractOwnerships?.forEach((lo: WIContractOwnership) => {
                let tempOwnership = lo.orriOwnerships.find((wio: WIContractOwnership) => wio.id === refId);
                if (tempOwnership) {
                    ownership = tempOwnership;
                }
            });
        }

        return ownership;
    };

    const openOwnershipNotes = (ownershipId: number) => {
        // this.setState({ ownershipNotesId: ownershipId });
        setSelectedWIOwnershipId(ownershipId);
        dialogContext.tract.openOwnershipNotes(
            findOwnership(ownershipId),
            solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.wiOwners,
            '',
            2,
            (ownership: Ownership) => onUpdateWINote(ownership)
        );
    };

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    return (
        <div className="lease-info-page">
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        {isDirty && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,0,0,.5)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div style={{ position: 'absolute', right: '.5rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button text="Cancel Changes" size="small" onClick={() => reset()} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Added to let the user know fields with * are required */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                        <span style={{ color: 'red', transform: 'scale(2)' }}>*</span>
                        <p>= Fields are required!</p>
                    </div>

                    <RHFLegend title="Solar Lease" />
                    <div hidden={true}>
                        <RHFInputBox label="" name="id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFCheckbox name="isGeneratedLease" label="" disabled={true} register={register} errors={errors} />
                    </div>
                    <div className="form-row">
                        {/* <RHFInputBox
                            label="Solar Lease Name"
                            name="solarLeaseName"
                            style={{ width: '100%'}}
                            register={register}
                            errors={errors}
                        /> */}
                        <RHFInputBox
                            label="Solar Lease Number"
                            name="solarLeaseNumber"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <RHFInputBox
                            label="Lease Date"
                            name="createdDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                        <RHFInputBox
                            label="Effective Date"
                            name="effectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                        <RHFInputBox label="Volume" name="volume" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Page" name="page" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Inst. Number" name="instrumentNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox
                            label="Recorded Date"
                            name="recordedDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFInputBox label="Fed / State Lease #" name="fedStateLeaseNumber" register={register} type="text" errors={errors} />
                        <RHFToggle label="Filed by Memorandum" name="isFiledByMemo" register={register} errors={errors} />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="Original Gross Acres"
                            name="grossAcres"
                            register={register}
                            type="number"
                            errors={errors}
                            step={0.0001}
                            isRequired={true}
                        />

                        <RHFInputBox
                            label="Original Lessor"
                            name="ownersOnDocument"
                            register={register}
                            type="text"
                            errors={errors}
                            isRequired={true}
                        />
                        <RHFInputBox
                            label="Original Lessee"
                            name="lesseeName"
                            register={register}
                            type="text"
                            errors={errors}
                        />
                        <RHFInputBox label="Lessor AKA" name="currentHold.lessorAKA" register={register} type="text" errors={errors} />
                    </div>
                    {/* TODO: Set this up for multiple Solar Lease Holds. */}
                    <RHFLegend title="Solar Lease Hold" />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div className="form-row">
                            <Button text="Previous" onClick={previousContractHold} size="small" intent="primary" />
                            {/* <span onClick={previousLeasehold} style={{ border: '1px solid black' }} className="clickable">Previous</span> */}
                            <span style={{ padding: '.5rem' }}>{`${currentLeaseHold + 1} of ${solarLeaseData?.solarLeaseHolds?.length || 1}`}</span>
                            <Button text="Next" onClick={nextContractHold} size="small" intent="primary" />
                            {/* <span onClick={nextLeasehold} style={{ border: '1px solid black' }} className="clickable">Next</span> */}
                        </div>
                        {solarLeaseData?.solarLeaseHolds?.length > 1 && (
                            <div style={{ display: 'flex' }}>
                                {copyLeaseholdClicked && (
                                    <div style={{textAlign: 'center', marginRight: '1rem'}}>
                                        <RHFSelect 
                                            onChange={(e) => handleSourceSelected(e)} 
                                            name="sourceLeaseholdId" 
                                            label="Source Leasehold" 
                                            options={leaseholdList()} 
                                            register={register} 
                                        />
                                        <Button 
                                            style={{marginTop: '1rem'}}
                                            disabled={!sourceSelected} 
                                            text="Confirm Copy"
                                            intent="danger" 
                                            onClick={onCopyLeasehold} 
                                        />
                                    </div>
                                )}
                                <div>
                                    {appContext.solarLeaseHoldId === 0 && (
                                        <Button
                                            style={{ marginLeft: '2rem' }}
                                            text="Copy Leasehold"
                                            onClick={() => setCopyLeaseholdClicked(true)}
                                            intent="primary" 
                                        />
                                    )}
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteSolarLeasehold()}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                        {solarLeaseData?.solarLeaseHolds?.length === 1 && (
                            <div style={{ display: 'flex' }}>
                                <div>
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteSolarLease()}
                                        intent="danger"
                                    />
                                </div>
                            </div>                        
                        )}
                    </div>
                    <div>
                        <div>
                            <RHFCheckbox 
                                style={{marginBottom: '2rem'}}
                                label={'Active Lease'} 
                                name={'currentHold.isActive'} 
                                register={register} 
                                errors={errors} 
                            />
                        </div>
                    </div>
                    <div hidden={true}>
                        <RHFInputBox label="" name="currentHold.id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.solarLeaseId" register={register} type="number" disabled={true} errors={errors} />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Due Diligence Date"
                            name="currentHold.dueDiligenceEffectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            onChange={() => handleExpirationChange('effective')}
                        />
                        <RHFInputBox
                            label="Due Diligence Term"
                            name="currentHold.dueDiligenceEffectiveTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            onChange={() => handleExpirationChange('effective')}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.dueDiligenceEffectiveTermTypeId"
                            options={getOptions('Time')}
                            onChange={() => handleExpirationChange('effective')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Exp. Date"
                            name="currentHold.dueDiligenceEffectiveExpirationDate"
                            register={register}
                            type="date"
                            errors={errors}
                            disabled={true}
                        />

                        <RHFInputBox
                            label="Due Diligence Fee"
                            name="currentHold.dueDiligenceFee"
                            register={register}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.dueDiligenceFeeTypeId"
                            options={getOptions('Fee', 'acre')}
                            // onChange={handleTotals}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Total"
                            name="currentHold.dueDiligenceFeeTotal"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={true}
                            step={0.0001}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Construction Date"
                            name="currentHold.constructionEffectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            onChange={() => handleExpirationChange('construction')}
                        />
                        <RHFInputBox
                            label="Construction Term"
                            name="currentHold.constructionTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            onChange={() => handleExpirationChange('construction')}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.constructionTermTypeId"
                            options={getOptions('Time')}
                            onChange={() => handleExpirationChange('construction')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Construction Exp. Date"
                            name="currentHold.constructionExpirationDate"
                            register={register}
                            type="date"
                            errors={errors}
                            disabled={true}
                        />

                        <RHFInputBox
                            label="Construction Fee"
                            name="currentHold.constructionFee"
                            register={register}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.constructionFeeTypeId"
                            options={getOptions('Fee', 'acre')}
                            // onChange={handleTotals}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Total"
                            name="currentHold.constructionFeeTotal"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={true}
                            step={0.0001}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Operation Date"
                            name="currentHold.operationEffectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            onChange={() => handleExpirationChange('operation')}
                        />
                        <RHFInputBox
                            label="Operation Term"
                            name="currentHold.operationTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            onChange={() => handleExpirationChange('operation')}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.operationTermTypeId"
                            options={getOptions('Time')}
                            onChange={() => handleExpirationChange('operation')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Operation Exp. Date"
                            name="currentHold.operationExpirationDate"
                            register={register}
                            type="date"
                            errors={errors}
                            disabled={true}
                        />

                        <RHFInputBox
                            label="Operating IPF Fee"
                            name="currentHold.operatingIPFFee"
                            register={register}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.operatingIPFFeeTypeId"
                            options={getOptions('Fee', 'acre')}
                            // onChange={handleTotals}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Total"
                            name="currentHold.operatingIPFFeeTotal"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={true}
                            step={0.0001}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Operation Extension Date"
                            name="currentHold.operationExtensionEffectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            onChange={() => handleExpirationChange('operationExtension')}
                        />
                        <RHFInputBox
                            label="Operation Term"
                            name="currentHold.operationExtensionTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            onChange={() => handleExpirationChange('operationExtension')}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.operationExtensionTermTypeId"
                            options={getOptions('Time')}
                            onChange={() => handleExpirationChange('operationExtension')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Extension Exp. Date"
                            name="currentHold.operationExtensionExpirationDate"
                            register={register}
                            type="date"
                            errors={errors}
                            disabled={true}
                        />

                        <RHFInputBox
                            label="Due Diligence Ext. Fee"
                            name="currentHold.dueDiligenceExtensionFee"
                            register={register}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.dueDiligenceExtensionFeeTypeId"
                            options={getOptions('Fee', 'acre')}
                            // onChange={handleTotals}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Total"
                            name="currentHold.dueDiligenceExtensionFeeTotal"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={true}
                            step={0.0001}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Option Date"
                            name="currentHold.optionEffectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            onChange={() => handleExpirationChange('option')}
                        />
                        <RHFInputBox
                            label="Option Term"
                            name="currentHold.optionTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            onChange={() => handleExpirationChange('option')}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.optionTermTypeId"
                            options={getOptions('Time')}
                            onChange={() => handleExpirationChange('option')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Exp. Date"
                            name="currentHold.optionExpirationDate"
                            register={register}
                            type="date"
                            errors={errors}
                            disabled={true}
                        />

                        <RHFInputBox
                            label="Operating OPF Fee"
                            name="currentHold.operatingOPFFee"
                            register={register}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                        <RHFSelect
                            label=""
                            name="currentHold.operatingOPFFeeTypeId"
                            options={getOptions('Fee', 'acre')}
                            // onChange={handleTotals}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Total"
                            name="currentHold.operatingOPFFeeTotal"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={true}
                            step={0.0001}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Escalating Pct"
                            name="currentHold.escalatingPct"
                            register={register}
                            type="number"
                            errors={errors}
                            step={0.0001}
                        />
                        <RHFInputBox
                            label="Bonus"
                            name="bonusAmount"
                            register={register}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                        <RHFSelect
                            label=""
                            name="bonusTypeId"
                            options={getOptions('Fee', 'acre')}
                            // onChange={handleTotals}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Rentals"
                            name="rentalAmount"
                            register={register}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                        <RHFSelect
                            label=""
                            name="rentalTypeId"
                            options={getOptions('Fee', 'acre')}
                            // onChange={handleTotals}
                            register={register}
                            errors={errors}
                        />
                    </div>

                    <div className="form-row">
                        <RHFSelect
                            label="Rental Frequency"
                            name="rentalFrequencyId"
                            options={getOptions('Frequency')}
                            // onChange={handleTotals}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox label="Rental Terms" name="rentalTerms" register={register} type="number" errors={errors} />
                        <RHFSelect label="" name="rentalTermTypeId" options={getOptions('Time')} register={register} errors={errors} />
                        <RHFInputBox label="Rent Due Date" name="rentDueDate" register={register} type="date" errors={errors} />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Addl Bonus Dscr"
                            name="addlBonusDescription"
                            register={register}
                            bodyStyle={{ width: '60%' }}
                            style={{ width: '100%' }}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Additional Bonus"
                            name="addlBonusAmount"
                            register={register}
                            bodyStyle={{ width: '20%' }}
                            style={{ width: '100%' }}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                        <RHFInputBox
                            label="Flat Bonus"
                            name="flatBonus"
                            register={register}
                            bodyStyle={{ width: '20%' }}
                            style={{ width: '100%' }}
                            type="number"
                            errors={errors}
                            // onChange={handleTotals}
                            step={0.0001}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Initial Bonus"
                            name="initialBonus"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={true}
                            step={0.0001}
                        />
                        <RHFInputBox
                            label="Rent Total"
                            name="totalRent"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={true}
                            step={0.0001}
                        />
                        <RHFInputBox
                            label="Total Bonus"
                            name="totalBonus"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={true}
                            step={0.0001}
                        />
                    </div>

                    <RHFLegend title="Original Legal Description" />
                    <div className="form-row">
                        <RHFTextArea
                            label={''}
                            name="legalDescription"
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                        />
                    </div>

                    <RHFLegend title="Solar Lease Hold Notes" />
                    <div className="form-row" style={{ width: '100%' }}>
                        <RHFTextArea
                            label={''}
                            name="currentHold.notes"
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                        />
                    </div>

                    <RHFLegend title="Document Management" />
                    {solarLeaseData?.solarLeaseHolds && (
                        <ContractHoldDocs
                            showDialog={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            hideDialog={() => dispatch(actions.layout.hideDialog())}
                            contractLookupId={solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.contractLookupId}
                            contractType={'Contract Hold'}
                        />
                    )}

                    <div style={{ minHeight: '20rem', marginTop: '2rem' }}>
                        <RHFLegend title="Contract Ownership" />
                        <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                        <ContractWorkingInterestList
                            isGovStateBlm={false}
                            owners={solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.wiOwners}
                            wiContractOwnerships={solarLeaseData?.solarLeaseHolds?.[currentLeaseHold]?.contractOwnerships}
                            prefix={'currentHold'}
                            setDialogShowing={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            tryAddWI={onTryAddWI}
                            onAddORRI={onAddORRI}
                            onCopyORRI={onCopyORRI}
                            onDeleteORRI={onDeleteORRI}
                            deleteContractOwnership={onDeleteContractOwnership}
                            openOwnershipNotes={openOwnershipNotes}
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            setValue={setValue}
                            watch={watch}
                            royalty={royalty}
                            contractType="solarLease"
                        />
                    </div>

                    <div style={{ marginTop: '5rem' }}>
                        <Button text="Save" type="submit" intent="primary" />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SolarLeaseInfo;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/SolarLeaseInfo.tsx